import React, { useState, useEffect, useRef } from 'react';

const CircleIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3"/></svg>;
const ListIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M4 6h16M4 10h16M4 14h10M4 18h6"/></svg>;
const ClockIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>;
const PhoneIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2A19.7 19.7 0 014 5.18 2 2 0 016 3h3a2 2 0 012 1.72c.13.96.36 1.9.71 2.81a2 2 0 01-.45 2.11L10 11a16 16 0 005.5 5.5l1.36-1.26a2 2 0 012.11-.45c.9.35 1.85.58 2.81.71A2 2 0 0122 16.92z"/></svg>;
const FormIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 9h6M9 12h6M9 15h4"/></svg>;
const BarIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M18 20V10M12 20V4M6 20v-6"/></svg>;
const ChatIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
const CodeIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M10 20l4-16M4 9h16M4 15h16"/></svg>;
const MessageIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>;
const ShieldIcon = () => <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;

const features = [
  {
    id: 'dialling', label: 'Campaign dialling', tag: 'MODULE_01',
    title: 'Intelligent campaign dialling',
    tags: ['preview-dial', 'auto-call', 'queue-mgmt'],
    desc: 'Manual preview, auto-dial, configurable timing. Agents stay in conversations, not menus.',
    icon: <CircleIcon />,
  },
  {
    id: 'sql', label: 'SQL strategy builder', tag: 'MODULE_02',
    title: 'SQL-based lead targeting',
    tags: ['sql-rules', 'custom-fields', 'segmentation'],
    desc: 'Build dialling rules from your own data. Custom fields, interaction history, shared campaigns.',
    icon: <ListIcon />,
  },
  {
    id: 'retry', label: 'Outcome iteration', tag: 'MODULE_03',
    title: 'Smart outcome retry engine',
    tags: ['smart-retry', 'outcome-rules', 'auto-iterate'],
    desc: 'Configurable retry logic based on call outcomes. 5min → 1hr → 2hr → custom cadences.',
    icon: <ClockIcon />,
  },
  {
    id: 'cli', label: 'CLI rotation', tag: 'MODULE_04',
    title: 'Dynamic CLI rotation engine',
    tags: ['dynamic-rotation', 'spam-reduction', 'schedule-based'],
    desc: 'Rotate caller IDs by outcome, schedule, and performance. Kill spam flagging permanently.',
    icon: <PhoneIcon />,
  },
  {
    id: 'forms', label: 'Webform builder', tag: 'MODULE_05',
    title: 'Embedded agent webforms',
    tags: ['custom-scripts', 'compliance-forms', 'embedded'],
    desc: 'Build scripts and capture forms directly in the agent UI. Sales, compliance, notes — all in one screen.',
    icon: <FormIcon />,
  },
  {
    id: 'reporting', label: 'Reporting suite', tag: 'MODULE_06',
    title: 'Enterprise reporting',
    tags: ['agent-kpis', 'campaign-reports', 'live-data'],
    desc: 'Agent performance, campaign outcomes, conversions. Live data, not sampled estimates.',
    icon: <BarIcon />,
  },
  {
    id: 'omni', label: 'Omnichannel', tag: 'MODULE_07',
    title: 'Unified omnichannel',
    tags: ['voice', 'sms', 'whatsapp', 'webchat', 'email'],
    desc: 'Voice, SMS, WhatsApp, webchat, and email in one agent view. Full cross-channel conversation history.',
    icon: <ChatIcon />,
  },
  {
    id: 'api', label: 'API-first', tag: 'MODULE_08',
    title: 'API-first architecture',
    tags: ['rest-api', 'crm-sync', 'lead-ingestion'],
    desc: 'CRM integrations, lead ingestion, custom automation. Built to fit your stack, not ours.',
    icon: <CodeIcon />,
  },
  {
    id: 'audit', label: 'Audit & compliance', tag: 'MODULE_09',
    title: 'Audit logs & exception tracking',
    tags: ['audit-logs', 'exception-tracking', 'compliance'],
    desc: 'Every action logged, timestamped, queryable. Full transparency for compliance and QA.',
    icon: <ShieldIcon />,
  },
  {
    id: 'whatsapp', label: 'WhatsApp messaging', tag: 'MODULE_10',
    title: 'WhatsApp Business Platform messaging',
    tags: ['whatsapp-api', 'send-receive', 'crm-sync'],
    desc: 'DialSpark offers WhatsApp Business Platform messaging through the same API as voice — send and receive WhatsApp messages from your CRM, contact centre or dialler.',
    icon: <MessageIcon />,
  },
];

export default function Features() {
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);
  const f = features[active];

  const resetTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive(prev => (prev + 1) % features.length);
    }, 5000);
  };

  useEffect(() => {
    resetTimer();
    return () => clearInterval(timerRef.current);
  }, []);

  const handleTabClick = (i) => {
    setActive(i);
    resetTimer();
  };

  return (
    <section id="platform" style={{ maxWidth: 960, margin: '0 auto', padding: '5rem 2.5rem' }}>
      <div style={{ marginBottom: '2.5rem' }}>
        <div style={tagStyle}>// PLATFORM MODULES</div>
        <h2 style={headStyle}>Every capability <span style={{ color: '#0a9e8f' }}>your team needs.</span></h2>
      </div>

      <div className="features-layout" style={{ gap: 1, background: 'rgba(10,158,143,0.13)', border: '1px solid rgba(10,158,143,0.13)', borderRadius: 12, overflow: 'hidden' }}>
        {/* Tabs */}
        <div className="features-tabs" style={{ background: '#0a0a0a', display: 'flex', flexDirection: 'column' }}>
          {features.map((feat, i) => (
            <button key={feat.id} onClick={() => handleTabClick(i)} style={{
              padding: '0.9rem 1.1rem', background: active === i ? '#141414' : '#0a0a0a',
              borderLeft: active === i ? '2px solid #077a6e' : '2px solid transparent',
              borderTop: 'none', borderRight: 'none', borderBottom: '1px solid rgba(10,158,143,0.13)',
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', textAlign: 'left', transition: 'background .15s',
            }}
              onMouseEnter={e => { if (active !== i) e.currentTarget.style.background = '#161616'; }}
              onMouseLeave={e => { if (active !== i) e.currentTarget.style.background = '#0a0a0a'; }}>
              <div style={{ width: 26, height: 26, borderRadius: 6, background: active === i ? 'rgba(10,158,143,0.25)' : 'rgba(10,158,143,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: active === i ? '#0a9e8f' : '#077a6e' }}>
                {feat.icon}
              </div>
              <span style={{ fontSize: '0.78rem', fontWeight: active === i ? 600 : 400, color: active === i ? '#f0f0f0' : '#c0c0c0', lineHeight: 1.3 }}>{feat.label}</span>
            </button>
          ))}
        </div>

        {/* Panel */}
        <div style={{ background: '#060606', padding: '2rem 2.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '0.65rem', color: '#077a6e', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em', marginBottom: 10 }}>{f.tag} // ACTIVE</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#f0f0f0', letterSpacing: '-0.025em', marginBottom: 10 }}>{f.title}</div>
            <div style={{ fontSize: '0.875rem', color: '#c0c0c0', lineHeight: 1.7, marginBottom: '1.5rem', fontWeight: 300, maxWidth: 460 }}>{f.desc}</div>

            {/* Tags */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {f.tags.map(t => (
                <span key={t} style={{ background: 'rgba(10,158,143,0.1)', border: '1px solid rgba(10,158,143,0.28)', color: '#0a9e8f', fontSize: '0.65rem', padding: '3px 10px', borderRadius: 4, fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.04em' }}>{t}</span>
              ))}
            </div>
          </div>

          {/* Auto-advance progress bar */}
          <div style={{ marginTop: '2rem', height: 2, background: 'rgba(10,158,143,0.1)', borderRadius: 2, overflow: 'hidden' }}>
            <div key={active} style={{ height: '100%', background: '#0a9e8f', borderRadius: 2, animation: 'featureProgress 5s linear forwards' }} />
          </div>
        </div>
      </div>
    </section>
  );
}

const tagStyle = { fontSize: '0.68rem', color: '#077a6e', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.1em', marginBottom: 12, borderLeft: '2px solid #056055', paddingLeft: 10 };
const headStyle = { fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.15, color: '#f0f0f0', marginBottom: 8 };
const subStyle = { fontSize: '0.875rem', color: '#909090', fontWeight: 300 };

const _css = `@keyframes featureProgress { from { width: 0% } to { width: 100% } }`;
if (typeof document !== 'undefined' && !document.getElementById('fp-style')) {
  const s = document.createElement('style');
  s.id = 'fp-style';
  s.textContent = _css;
  document.head.appendChild(s);
}
